/**
 * Where OS stands on each page, in the agent's vocabulary — the phone's
 * `recordStackPlaceOf` (`lib/agent-place.ts`), read off this app's paths
 * rather than the phone's route names. A fresh conversation opens on it, so
 * its first offer is about the page the person is on, as the phone's door
 * does.
 *
 * Two questions, answered apart (web-redesign-08-os.md, Decided 4):
 * - `placeOfPath`: what a fresh conversation is about, on every app page OS
 *   can float over. The OS page is OS already, so nothing floats there.
 * - `hasSidePlace`: whether the page has a place of its own for the side
 *   sheet to stand beside — a record, a list, Team, Roles, What OS knows or an
 *   insight. Anywhere else, OS left in the side sheet floats instead.
 *
 * And, for live mode (os-live-mode.md 2.3), where the person is NOW:
 * - `livePlaceOf`: the page under the router's location, which a call follows
 *   as the person moves; on the OS page, the thread's own place.
 * - `viewOf`: what that page shows, as data, when it reported one.
 */
import type { AgentPlace, AgentView } from '@alluzo/shared';

export type DockPlace = { readonly screen: AgentPlace; readonly recordId: string | null };

const OS_PAGE = /^\/coach(\/|$)/;
/** One insight on its own page (web-redesign-10 phase 5): not Add insight, not Arrange, not its editor. */
const OPENED_INSIGHT = /^insights\/(?!new\/?$|arrange\/?$)[^/]+\/?$/;
const RECORD = /^\/(?:records|lists)\/([^/]+)(?:\/(.*))?$/;
/** One template open on its own page, or being changed (D24 of docs/plans/report-templates.md): not the tab, not Generate new template. */
const OPENED_TEMPLATE = /^report-templates\/(?!new\/?$)[^/]+(\/.*)?$/;

/** A record's own pages (and a list's, which is a record): the screen under its address. */
const recordPlaceOf = (pathname: string): DockPlace | null => {
  const match = RECORD.exec(pathname);
  const recordId = match?.[1];
  if (recordId === undefined || recordId === '') return null;
  const rest = match?.[2] ?? '';
  const screen: AgentPlace = rest.startsWith('new-entry')
    ? 'new_entry'
    : rest.startsWith('entries/')
      ? 'entry'
      : rest.startsWith('customize')
        ? 'customize'
        : OPENED_TEMPLATE.test(rest)
            ? 'document'
            : rest.startsWith('report-templates')
              ? 'documents'
          : OPENED_INSIGHT.test(rest)
            ? 'insight'
            : rest.startsWith('insights')
              ? 'insights'
              : 'record';
  return { screen, recordId: decodeURIComponent(recordId) };
};

const WORKSPACE_PLACES: readonly (readonly [RegExp, AgentPlace])[] = [
  [/^\/business\/(team|people|invite)(\/|$)/, 'team'],
  // Roles, today at the jobs' address: a role is a job by its new name.
  [/^\/business\/(roles|jobs)(\/|$)/, 'roles'],
  [/^\/business\/knows\/?$/, 'knows'],
  [/^\/business\/?$/, 'business'],
  [/^\/dashboard\/(?!new\/?$|arrange\/?$)[^/]+\/?$/, 'insight'],
  [/^\/dashboard\/[^/]+/, 'insights'],
  [/^\/dashboard\/?$/, 'dashboard'],
  [/^\/account(\/|$)/, 'account'],
  // Routines and a routine's own page (docs/plans/routines.md R9).
  [/^\/routines(\/|$)/, 'routines'],
];

/** What a fresh conversation opened on this page is about, or null on the OS page, where nothing floats. */
export const placeOfPath = (pathname: string): DockPlace | null => {
  if (OS_PAGE.test(pathname)) return null;
  const record = recordPlaceOf(pathname);
  if (record !== null) return record;
  for (const [path, screen] of WORKSPACE_PLACES) if (path.test(pathname)) return { screen, recordId: null };
  return { screen: 'home', recordId: null };
};

/** The pages with a place of their own: a record, a list, Team, Roles, What OS knows, an insight, Routines. */
const SIDE_PLACES: readonly RegExp[] = [
  /^\/routines(\/|$)/,
  /^\/(?:records|lists)\/[^/]+/,
  /^\/lists\/?$/,
  /^\/business\/(team|people|roles|jobs)(\/|$)/,
  /^\/business\/knows\/?$/,
  /^\/dashboard\/[^/]+/,
];

export const hasSidePlace = (pathname: string): boolean => SIDE_PLACES.some((path) => path.test(pathname));

/**
 * Where a live call is, from the router's location rather than the thread's
 * first screen: the page the person is on now. The OS page floats over
 * nothing, so there the thread's own place stands.
 */
export const livePlaceOf = (pathname: string, thread: LivePlace): LivePlace => placeOfPath(pathname) ?? thread;

/** A place a call can be told: a page's, or a thread's own (whose screen may be any the thread was opened on). */
export type LivePlace = { readonly screen: string; readonly recordId: string | null };

/**
 * What the page at this place shows, as data: the view a page reported
 * (`agent-view-store`'s `held`), only when it is this place's record.
 */
export const viewOf = (place: LivePlace, held: { readonly recordId: string; readonly view: AgentView } | null): AgentView | undefined =>
  held !== null && place.recordId !== null && held.recordId === place.recordId ? held.view : undefined;
